import { getCurrentSchoolYear } from "composables/useHelpers";
import { useAuthStore } from "core/auth/auth.store";
import { clamp, cloneDeep } from "lodash";
import { storeToRefs } from "pinia";
import { useGlobalSchoolDataStore } from "store/modules/globalSchoolDataStore";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import { computed, onMounted, ref } from "vue";
import { Cluster } from "./clusterModel";
import { createDefaultManager } from "./defaultManager";
import { createGridManager } from "./gridManager";
import { currentUserToDto } from "core/auth/auth.mapper";

const createClusterManager = () => {
  const { weeks, deltaState, fetchAndSetWeeks, startDelta, endDelta } =
    createDefaultManager();

  const {
    grid,
    hasGridGroups,
    isRendered,
    initializeGrid,
    removeWeekFromRow,
    addWeekToRow,
    removeItemFromGrid,
    generateGridGroup,
    toggleGroupVisibility,
    findGridGroupIdByIndex,
    addGridItem,
    trimTrailingEmptyRows,
    rebuildGridFromClusters,
    rebuildGroupFromClusters,
  } = createGridManager();

  // Cluster Refs
  const clusters = ref([]);
  const lessonGroups = ref([]);
  const lessonGroupClusters = ref([]);
  const selectedSubject = ref(null);
  const isFetchingClusters = ref(false);
  const isInitializing = ref(false);
  const isInitialized = ref(false);

  // Live preview while dragging on the "add cluster" bar to size a new cluster.
  // null when idle, otherwise { groupId, rowIndex, startIndex, duration }.
  const dragPreview = ref(null);

  const setDragPreview = (preview) => {
    dragPreview.value = preview;
  };

  const clearDragPreview = () => {
    dragPreview.value = null;
  };

  // Snapshot of cluster row positions taken right before adding a new cluster,
  // so a discarded add can revert the clusters that were bumped down (and the
  // extra row that was generated). Cleared on a successful save.
  const addSnapshot = ref(null);

  const captureAddSnapshot = () => {
    addSnapshot.value = clusters.value.map((cluster) => ({
      id: cluster.id,
      rowIndex: cluster.planningMeta.rowIndex,
    }));
  };

  const clearAddSnapshot = () => {
    addSnapshot.value = null;
  };

  const restoreAddSnapshot = () => {
    if (!addSnapshot.value) {
      return;
    }

    const snapshotIds = new Set(addSnapshot.value.map(({ id }) => id));

    // Drop clusters added after the snapshot (the discarded temporary cluster).
    clusters.value = clusters.value.filter((cluster) =>
      snapshotIds.has(cluster.id),
    );

    // Restore the pre-add row positions of the clusters that got bumped.
    addSnapshot.value.forEach(({ id, rowIndex }) => {
      const cluster = clusters.value.find((item) => item.id === id);

      if (cluster) {
        cluster.planningMeta.rowIndex = rowIndex;
      }
    });

    rebuildGridFromClusters(clusters.value);
    addSnapshot.value = null;
  };

  // Initialize the cluster manager
  const init = async ({
    force = false,
    schoolYear = getCurrentSchoolYear(),
  } = {}) => {
    if (isInitializing.value) {
      return isInitializing.value;
    }

    if (isInitialized.value && !force) {
      return;
    }

    isInitializing.value = (async () => {
      // Fetch weeks, lesson groups
      await Promise.all([fetchAndSetWeeks(), fetchAndSetLessonGroups()]);

      // Fetch and set clusters
      await fetchClusters({ force: true, schoolYear });

      // Initialize the grid
      initializeGrid(clusters.value, weeks.value, lessonGroupClusters.value);

      // Default the subject filter to the first available subject
      selectedSubject.value = subjects.value[0]?.value ?? null;

      isInitializing.value = false;
    })();

    isInitialized.value = true;
  };

  const fetchAndSetLessonGroups = async () => {
    const globalSchoolDataStore = useGlobalSchoolDataStore();
    const { fetchLearningObjectivesLessongroups } = globalSchoolDataStore;

    let fetchedLessonGroups = [];

    try {
      fetchedLessonGroups = await fetchLearningObjectivesLessongroups();
    } catch (error) {
      console.log("🚀 ~ fetchAndSetLessonGroups ~ error:", error);
    }

    lessonGroups.value = fetchedLessonGroups;

    return lessonGroups.value;
  };

  const generateClusterGroups = async (lessonGroups) => {
    lessonGroupClusters.value = lessonGroups.map(
      ({
        name: id,
        lesson_class: name,
        subject,
        subject_name,
        education_levels,
      }) => {
        const meta = {
          subject,
          subjectName: subject_name,
          // The klas (e.g. "HAVO 4") a leerdoel added here prefills — it matches
          // the Klassen select options (both derive from calculateEducationLevel).
          educationLevel: education_levels?.[0] ?? null,
        };

        return generateGridGroup({ id, name, meta });
      },
    );
  };

  // Unique subjects across all grid groups, used by the planning subject filter bar
  const subjects = computed(() => {
    // Without lesson groups the grid holds plain rows, not groups with meta.
    if (!hasGridGroups.value) {
      return [];
    }

    const uniqueSubjects = new Map();

    grid.value.forEach(({ meta: { subject, subjectName } }) => {
      if (subject && !uniqueSubjects.has(subject)) {
        uniqueSubjects.set(subject, {
          value: subject,
          label: subjectName || subject,
        });
      }
    });

    return Array.from(uniqueSubjects.values());
  });

  // Grid groups filtered by the selected subject (null = show all subjects)
  const filteredGrid = computed(() => {
    if (!selectedSubject.value) {
      return grid.value;
    }

    return grid.value.filter(
      ({ meta: { subject } }) => subject === selectedSubject.value,
    );
  });

  const setSelectedSubject = (subject = null) => {
    selectedSubject.value = subject;
  };

  const setDraggingStateOnClusterGroup = (groupId, value) => {
    findClusterGroupById(groupId).isDragging = value;
  };

  const findClusterGroupById = (id = null) => {
    if (!id) {
      throw new Error("Id is required to find a cluster group");
    }

    const foundClusterGroup = lessonGroupClusters.value.find(
      ({ id: groupId }) => groupId === id,
    );

    if (foundClusterGroup === undefined) {
      throw new Error(`Cluster group with id: ${id} not found`);
    }

    return foundClusterGroup;
  };

  const fetchClusters = async ({
    force = false,
    schoolYear = getCurrentSchoolYear(),
  } = {}) => {
    if (isFetchingClusters.value && !force) {
      return isFetchingClusters.value;
    }

    isFetchingClusters.value = (async () => {
      const learningObjectivesStore = useLearningObjectivesStore();
      const { fetchClusters } = learningObjectivesStore;

      try {
        const fetchedClusters = await fetchClusters({
          forceFetch: force,
          schoolYear,
        });

        // Uncomment the code below to add lesson groups to the grid based on the fetched clusters
        fetchedClusters.forEach(({ lesson_class, subject_shortage }) => {
          if (
            lessonGroups.value.find(
              ({ lesson_class: _lessonClass, subject }) =>
                _lessonClass === lesson_class && subject === subject_shortage,
            )
          ) {
            return;
          }

          lessonGroups.value.push({
            lesson_class,
            name: lesson_class,
            subject: subject_shortage,
          });
        });

        // This will create groups in the grid based on the lesson groups
        generateClusterGroups(lessonGroups.value);

        setFetchedClusters(fetchedClusters);

        return fetchedClusters;
      } catch (error) {
        console.log("🚀 ~ isFetching.value['clusters']= ~ error:", error);
      } finally {
        isFetchingClusters.value = false;
      }
    })();

    return isFetchingClusters.value;
  };

  const setFetchedClusters = (data) => {
    clusters.value = [];

    if (!Array.isArray(data)) {
      throw new Error("Clusters must be an array");
    }

    data.forEach((cluster) =>
      clusters.value.push(new Cluster({ ...cluster, temporary: false })),
    );

    return clusters.value;
  };

  const addCluster = (data) => {
    // Auth store
    const authStore = useAuthStore();
    const { user } = storeToRefs(authStore);

    // The user object is now a client-side object using camelCased keys instead of snake_cased
    // Our learning objectives frontend still relies on snaked_cased keys
    const revertCurrentUserDtoMap = currentUserToDto(user.value);

    // We add the current user to the cluster, only for temporary clusters
    // Once saved the data will be updated with the correct user
    // and passed back from the backend
    data.user = cloneDeep(revertCurrentUserDtoMap);

    const newCluster = new Cluster(data);

    clusters.value.push(newCluster);

    // Return the reactive proxy (not the raw instance) so later mutations —
    // e.g. Object.assign in store() after saving — trigger grid re-renders.
    return clusters.value[clusters.value.length - 1];
  };

  const updateDelta = (cluster, changeInColumns, handleSide, deltaX) => {
    if (!cluster || !deltaState[cluster.id] || !changeInColumns) {
      return;
    }

    const {
      id: clusterId,
      planningMeta: { weekIndexes },
    } = cluster;
    const {
      startWeekIndex: currentStartWeekIndex,
      endWeekIndex: currentEndWeekIndex,
    } = deltaState[clusterId];

    // Update previous deltaStart with the current deltaX
    deltaState[clusterId].deltaStart = deltaX;

    if (handleSide === "end") {
      const newEndIndex = clamp(
        currentEndWeekIndex + changeInColumns,
        weekIndexes[0],
        weeks.value.length - 1,
      );

      // Maybe check a different way to pass the removeWeekFromRow and addWeekToRow
      cluster.updateEndWeekByIndex(
        newEndIndex,
        currentEndWeekIndex,
        removeWeekFromRow,
        addWeekToRow,
      );

      deltaState[clusterId].endWeekIndex = newEndIndex;
    }

    if (handleSide === "start") {
      const newStartIndex = clamp(
        currentStartWeekIndex + changeInColumns,
        0,
        weekIndexes[weekIndexes.length - 1],
      );

      // Maybe check a different way to pass the removeWeekFromRow and addWeekToRow
      cluster.updateStartWeekByIndex(
        newStartIndex,
        currentStartWeekIndex,
        removeWeekFromRow,
        addWeekToRow,
      );

      deltaState[clusterId].startWeekIndex = newStartIndex;
    }
  };

  // --- Live grid-snapped move with reflow -------------------------------------
  // The bar follows the cursor, snapping to week columns (x) and stacking rows
  // (y). Other clusters in the same klas are pushed down while the dragged bar
  // overlaps them and spring back to their resting row when the space frees up.
  let moveState = null;

  const beginMove = (cluster) => {
    const { groupId } = cluster.planningMeta;

    const groupClusters = clusters.value.filter(
      (item) =>
        item.planningMeta.groupId === groupId &&
        item.subject_shortage === cluster.subject_shortage,
    );

    moveState = {
      id: cluster.id,
      startWeekIndex: cluster.planningMeta.weekIndexes[0],
      rowIndex: cluster.planningMeta.rowIndex,
      duration: cluster.duration,
      // Resting layout at grab time; every reflow is derived from this, so bars
      // return to these rows once they no longer collide with the dragged bar.
      base: groupClusters.map((item) => ({
        cluster: item,
        startWeekIndex: item.planningMeta.weekIndexes[0],
        rowIndex: item.planningMeta.rowIndex,
        duration: item.duration,
      })),
    };
  };

  const endMove = () => {
    moveState = null;
  };

  const weeksOverlap = (aStart, aDuration, bStart, bDuration) =>
    aStart <= bStart + bDuration - 1 && bStart <= aStart + aDuration - 1;

  const moveDelta = (cluster, changeInColumns, changeInRows) => {
    if (!moveState || moveState.id !== cluster.id) {
      return;
    }

    const targetStart = clamp(
      moveState.startWeekIndex + changeInColumns,
      0,
      weeks.value.length - moveState.duration,
    );

    const maxBaseRow = Math.max(0, ...moveState.base.map((b) => b.rowIndex));
    const targetRow = clamp(
      moveState.rowIndex + changeInRows,
      0,
      maxBaseRow + 1,
    );

    // Nothing changed cell-wise since the last step.
    if (
      targetStart === cluster.planningMeta.weekIndexes[0] &&
      targetRow === cluster.planningMeta.rowIndex
    ) {
      return;
    }

    // The dragged bar owns its target row; each other bar keeps its resting row
    // unless it collides with an already-placed bar, then drops to the next free
    // row (cascading downward).
    const placements = new Map([
      [
        cluster.id,
        { row: targetRow, start: targetStart, duration: moveState.duration },
      ],
    ]);

    moveState.base
      .filter((other) => other.cluster.id !== cluster.id)
      .sort(
        (a, b) =>
          a.rowIndex - b.rowIndex || a.startWeekIndex - b.startWeekIndex,
      )
      .forEach((other) => {
        let row = other.rowIndex;

        while (
          [...placements.values()].some(
            (placed) =>
              placed.row === row &&
              weeksOverlap(
                placed.start,
                placed.duration,
                other.startWeekIndex,
                other.duration,
              ),
          )
        ) {
          row += 1;
        }

        placements.set(other.cluster.id, {
          row,
          start: other.startWeekIndex,
          duration: other.duration,
        });
      });

    // Apply the computed layout, then re-render only the dragged klas — clusters
    // never cross klassen, so no other group changes.
    moveState.base.forEach(({ cluster: model }) => {
      const placement = placements.get(model.id);

      model.planningMeta.rowIndex = placement.row;
      model.moveToStartIndex(placement.start);
    });

    rebuildGroupFromClusters(cluster.planningMeta.groupIndex, clusters.value);
  };

  const updateClusterPlanning = async () => {
    return await Promise.all(clusters.value.map((cluster) => cluster.upsert()));
  };

  // Persist changes as soon as a move/resize is released. upsert() skips
  // unchanged clusters, so this only stores the ones actually affected.
  const persistOnRelease = async () => {
    try {
      await updateClusterPlanning();
    } catch (error) {
      console.log("🚀 ~ persistOnRelease ~ error:", error);
    }
  };

  const findClusterById = (id) => {
    const foundCluster = clusters.value.find(
      (cluster) => cluster.id === parseInt(id),
    );

    if (foundCluster === undefined) {
      throw new Error(`Cluster with id: ${id} not found`);
    }

    return foundCluster;
  };

  const removeCluster = async (clusterId) => {
    const cluster = findClusterById(clusterId);

    // Delete cluster from the server
    try {
      await cluster.delete();

      const clusterIndex = clusters.value.findIndex(
        ({ id }) => id === clusterId,
      );

      if (clusterIndex === -1) {
        throw new Error(`Cluster with id: ${clusterId} not found`);
      }

      // Remove from the local store
      clusters.value.splice(clusterIndex, 1);

      // Remove from the grid
      const { rowIndex, weekIndexes } = cluster.planningMeta;

      removeItemFromGrid(rowIndex, weekIndexes, cluster);

      // Drop the now-superfluous trailing row(s) so the class isn't left taller.
      trimTrailingEmptyRows(cluster);
    } catch (error) {
      console.log("🚀 ~ removeCluster ~ error:", error);
    }
  };

  return {
    clusters,
    lessonGroups,
    weeks,
    deltaState,
    grid,
    subjects,
    filteredGrid,
    selectedSubject,
    setSelectedSubject,
    hasGridGroups,
    isRendered,
    isInitializing,
    init,
    startDelta,
    endDelta,
    updateDelta,
    beginMove,
    moveDelta,
    endMove,
    persistOnRelease,
    removeItemFromGrid,
    toggleGroupVisibility,
    findGridGroupIdByIndex,
    addGridItem,
    updateClusterPlanning,
    addCluster,
    findClusterById,
    removeCluster,
    setDraggingStateOnClusterGroup,
    dragPreview,
    setDragPreview,
    clearDragPreview,
    addSnapshot,
    captureAddSnapshot,
    clearAddSnapshot,
    restoreAddSnapshot,
  };
};

export const clusterManager = ref(createClusterManager());

export const useClusterManager = () => {
  const isInitialized = ref(false);

  onMounted(async () => {
    await clusterManager.value.init();

    isInitialized.value = true;
  });

  return { clusterManager, isInitialized };
};
